import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { logger } from '../utils/logger';
import { bindTermlyConsent } from '../utils/consent';

const TermlyScript: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    // Only block specific app shells where the CMP would interfere with auth headers/loading
    const blockedPrefixes = ['/projects'];
    const isBlocked = blockedPrefixes.some(prefix => location.pathname.startsWith(prefix));

    // Don't load Termly on project/production shells
    if (isBlocked) {
      return;
    }

    // Only load Termly if we have a site ID configured
    const termlySiteId = import.meta.env.VITE_TERMLY_SITE_ID;
    
    if (!termlySiteId) {
      logger.warn('Termly Site ID not configured. Skipping Termly CMP initialization.');
      return;
    }

    // Check if Termly script already exists
    if (document.querySelector('script[data-termly-script]')) {
      bindTermlyConsent();
      return;
    }

    const loadTermly = () => {
      if (document.querySelector('script[data-termly-script]')) return;
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = `https://app.termly.io/resource-blocker/${termlySiteId}?autoBlock=on`;
      script.setAttribute('data-termly-script', 'true');
      script.async = true;
      script.onload = bindTermlyConsent;
      document.head.appendChild(script);
    };

    const deferOnPublicEntry = location.pathname === '/' || location.pathname.startsWith('/mcp');
    if (!deferOnPublicEntry) {
      loadTermly();
      return;
    }

    let idleId: number | ReturnType<typeof setTimeout> | undefined;
    const schedule = () => {
      if ('requestIdleCallback' in window) {
        idleId = window.requestIdleCallback(loadTermly, { timeout: 2000 });
      } else {
        idleId = globalThis.setTimeout(loadTermly, 1000);
      }
    };

    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true });

    return () => {
      window.removeEventListener('load', schedule);
      if (idleId !== undefined) {
        if ('cancelIdleCallback' in window) window.cancelIdleCallback(idleId as number);
        else globalThis.clearTimeout(idleId);
      }
    };
  }, [location.pathname]);

  return null;
};

export default TermlyScript;
